// 🔒 LOCKED V1 — DO NOT MODIFY WITHOUT REVIEW
// Any new feature must be additive, not modifying existing structure

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import Icon from '../../components/AppIcon';
import StatsCard from '../../components/ui/StatsCard';
import StatusFilterDropdown from '../../components/ui/StatusFilterDropdown';
import ChangeOrdersTable from './components/ChangeOrdersTable';
import AddChangeOrderModal from './components/AddChangeOrderModal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import subcontractChangeOrderService from '../../services/subcontractChangeOrderService';
import subcontractService from '../../services/subcontractService';
import { projectService } from '../../services/projectService';
import projectExtrasService from '../../services/projectExtrasService';
import { supabase } from '../../lib/supabase';
import useTranslation from 'hooks/useTranslation';
import { formatCurrency as fmtCAD } from '../../utils/formatCurrency';
import { EXTRA_DIRECTIVE_EMBED } from '../../utils/extraDirectiveNumber';

const ALLOWED_SORT_KEYS = ['co_number', 'subcontractor', 'trade', 'amount', 'status', 'created_at'];
const ALLOWED_SORT_DIRS = ['asc', 'desc'];

// ─── SubcontractorDropdown ─────────────────────────────────────────────────────
const SubcontractorDropdown = ({ subcontracts = [], value, onChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const containerRef = useRef(null);
  const listRef = useRef(null);

  const buildOptions = useCallback(() => {
    const opts = [{ id: '', label: t('changeOrders.allSubcontractors'), secondary: '' }];
    subcontracts?.forEach(sub => {
      const name = sub?.subcontractor?.companyName || sub?.name || t('changeOrders.unknownSubcontractor');
      const trade = sub?.subcontractor?.trade || sub?.projectCost?.costCode || '';
      const label = [name, trade]?.filter(Boolean)?.join(' · ');
      opts?.push({ id: sub?.id, label, name, trade });
    });
    return opts;
    // `t` entre dans les dependances : les libelles sont desormais traduits, et
    // sans lui la liste garderait la langue du premier rendu.
  }, [subcontracts, t]);

  const allOptions = buildOptions();
  const selectedOption = allOptions?.find(o => o?.id === (value ?? '')) || allOptions?.[0];

  const updateDropdownPosition = useCallback(() => {
    if (!containerRef?.current) return;
    const rect = containerRef?.current?.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 220;
    const spaceBelow = viewportHeight - rect?.bottom;
    const spaceAbove = rect?.top;
    const showAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
    if (showAbove) {
      setDropdownStyle({ position: 'fixed', top: rect?.top - dropdownHeight - 4, left: rect?.left, width: rect?.width, minWidth: '280px', maxHeight: `${dropdownHeight}px`, zIndex: 9999 });
    } else {
      setDropdownStyle({ position: 'fixed', top: rect?.bottom + 4, left: rect?.left, width: rect?.width, minWidth: '280px', maxHeight: `${dropdownHeight}px`, zIndex: 9999 });
    }
  }, []);

  useEffect(() => {
    if (open) updateDropdownPosition();
  }, [open, updateDropdownPosition]);

  useEffect(() => {
    if (!open) return;
    const handleUpdate = () => updateDropdownPosition();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);
    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [open, updateDropdownPosition]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (
        containerRef?.current && !containerRef?.current?.contains(e?.target) &&
        listRef?.current && !listRef?.current?.contains(e?.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const handleSelect = (opt) => {
    onChange(opt?.id || '');
    setOpen(false);
  };

  const dropdownContent = open ? (
    <div
      ref={listRef}
      style={{ ...dropdownStyle, overflowY: 'auto' }}
      className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
    >
      {allOptions?.map((opt) => {
        const isSelected = (value ?? '') === opt?.id;
        return (
          <button
            key={opt?.id ?? 'all'}
            type="button"
            onMouseDown={() => handleSelect(opt)}
            className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-center justify-between gap-2 ${
              isSelected ? 'bg-orange-50 text-primary' : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <span className={`truncate ${opt?.id === '' ? 'text-gray-400 italic' : ''}`}>
              {opt?.label}
            </span>
            {isSelected && (
              <Icon name="Check" size={10} className="shrink-0 text-primary" />
            )}
          </button>
        );
      })}
    </div>
  ) : null;

  return (
    <div ref={containerRef} className="relative flex-1 max-w-sm">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`w-full h-8 pl-3 pr-8 text-xs rounded-md border bg-white text-left truncate transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 ${
          open
            ? 'border-primary ring-2 ring-primary/20'
            : 'border-gray-200 hover:border-gray-300'
        } ${value ? 'text-gray-900' : 'text-gray-400'}`}
      >
        {selectedOption?.label || 'All'}
      </button>
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
        <Icon name="ChevronDown" size={13} className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </span>
      {typeof document !== 'undefined' && dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
};

const SubcontractChangeOrdersPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const initialProject = location?.state?.project;

  const getInitialSort = () => {
    const sortKey = searchParams?.get('sort');
    const sortDir = searchParams?.get('dir');
    if (ALLOWED_SORT_KEYS?.includes(sortKey) && ALLOWED_SORT_DIRS?.includes(sortDir)) {
      return { key: sortKey, direction: sortDir };
    }
    return { key: 'co_number', direction: 'asc' };
  };

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(initialProject || null);
  const [changeOrders, setChangeOrders] = useState([]);
  const [subcontracts, setSubcontracts] = useState([]);
  const [projectExtras, setProjectExtras] = useState([]);
  const [sortConfig, setSortConfig] = useState(getInitialSort());
  const [selectedSubcontractorId, setSelectedSubcontractorId] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Status filter options (subcontract_change_orders.status)
  const changeOrderStatusFilterOptions = [
    { value: 'all', label: t('changeOrders.filters.allStatuses'), dot: null },
    { value: 'draft', label: t('changeOrders.status.draft'), dot: '#9ca3af' },
    { value: 'sent', label: t('changeOrders.status.sent'), dot: '#3b82f6' },
    { value: 'signed', label: t('changeOrders.status.signed'), dot: '#22c55e' },
    { value: 'rejected', label: t('changeOrders.status.rejected'), dot: '#f87171' },
  ];
  const [showAddModal, setShowAddModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  // Subcontract filter from URL (set when navigating from Contracts page CO count)
  const subcontractFilter = searchParams?.get('subcontract') || null;

  useEffect(() => {
    const sortKey = searchParams?.get('sort');
    const sortDir = searchParams?.get('dir');
    const isValid = ALLOWED_SORT_KEYS?.includes(sortKey) && ALLOWED_SORT_DIRS?.includes(sortDir);
    if (!isValid) {
      setSearchParams({ sort: 'co_number', dir: 'asc' }, { replace: true });
    }
  }, []);

  useEffect(() => {
    const sortKey = searchParams?.get('sort');
    const sortDir = searchParams?.get('dir');
    if (ALLOWED_SORT_KEYS?.includes(sortKey) && ALLOWED_SORT_DIRS?.includes(sortDir)) {
      setSortConfig(prev => {
        if (prev?.key === sortKey && prev?.direction === sortDir) return prev;
        return { key: sortKey, direction: sortDir };
      });
    }
  }, [searchParams]);

  const handleSort = (key) => {
    if (!ALLOWED_SORT_KEYS?.includes(key)) return;
    const direction = sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
    setSearchParams({ sort: key, dir: direction }, { replace: true });
  };

  useEffect(() => {
    if (id) {
      fetchAll();
    }
  }, [id]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cos, subs, extras, projectData, directivesRes] = await Promise.all([
        subcontractChangeOrderService?.getByProjectId(id),
        subcontractService?.getByProjectId(id),
        projectExtrasService?.getByProjectId(id),
        projectService?.getById(id),
        // Nomenclatures des directives du projet (une requete) : la liste des
        // extras affiche le n° de directive comme le tableau.
        supabase?.from('project_directives')?.select('id, nomenclature')?.eq('project_id', id),
      ]);
      const nomenclatureById = Object.fromEntries((directivesRes?.data || [])?.map((d) => [d?.id, d?.nomenclature]));
      const extrasWithDirective = (extras || [])?.map((ex) => (
        ex?.directiveId && nomenclatureById?.[ex?.directiveId]
          ? { ...ex, directive: { nomenclature: nomenclatureById?.[ex?.directiveId] } }
          : ex
      ));

      // Backfill co_number for any existing COs that are missing it
      const cosWithNumbers = await backfillMissingCoNumbers(cos || [], subs || []);

      setChangeOrders(cosWithNumbers);
      setSubcontracts(subs || []);
      setProjectExtras(extrasWithDirective);
      if (projectData) setProject(projectData);
    } catch (err) {
      console.error('Error fetching change orders data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Backfill missing subcontract change orders for already-approved extras
  const backfillMissingChangeOrders = async (projectId, existingCos, subs) => {
    try {
      // Fetch all approved extras for this project
      const { data: approvedExtras, error: extrasError } = await supabase
        ?.from('project_extras')
        ?.select('id, title, project_id')
        ?.eq('project_id', projectId)
        ?.eq('status', 'approved');

      if (extrasError || !approvedExtras?.length) return existingCos;

      let updatedCos = [...existingCos];

      for (const extra of approvedExtras) {
        // Fetch extra lines with a subcontract_id
        const { data: lines, error: linesError } = await supabase
          ?.from('project_extra_lines')
          ?.select('id, subcontract_id, amount')
          ?.eq('extra_id', extra?.id)
          ?.not('subcontract_id', 'is', null);

        if (linesError || !lines?.length) continue;

        for (const line of lines) {
          // Check if a CO already exists for this extra + subcontract combo (in-memory first)
          const alreadyExists = updatedCos?.some(
            co => co?.sourceExtraId === extra?.id && co?.subcontractId === line?.subcontract_id
          );

          if (alreadyExists) continue;

          // Double-check in DB to avoid race conditions
          const { data: dbExisting } = await supabase
            ?.from('subcontract_change_orders')
            ?.select('id')
            ?.eq('source_extra_id', extra?.id)
            ?.eq('subcontract_id', line?.subcontract_id)
            ?.maybeSingle();

          if (dbExisting) continue;

          // Fetch subcontract cost_code for co_number generation
          const subcontract = subs?.find(s => s?.id === line?.subcontract_id);
          let costCode = subcontract?.projectCost?.costCode;

          if (!costCode) {
            const { data: subData } = await supabase
              ?.from('subcontracts')
              ?.select('id, project_costs(cost_code)')
              ?.eq('id', line?.subcontract_id)
              ?.single();
            costCode = subData?.project_costs?.cost_code || 'CO';
          }

          // Determine MAX AV number for this subcontract across all COs (in-memory + DB)
          const maxAvInMemory = updatedCos
            ?.filter(co => co?.subcontractId === line?.subcontract_id)
            ?.reduce((max, co) => {
              const match = co?.coNumber?.match(/\.AV(\d+)$/i);
              const num = match ? parseInt(match?.[1], 10) : 0;
              return num > max ? num : max;
            }, 0);

          const { data: allCosForSub } = await supabase
            ?.from('subcontract_change_orders')
            ?.select('co_number')
            ?.eq('subcontract_id', line?.subcontract_id);

          const maxAvInDb = (allCosForSub || [])?.reduce((max, co) => {
            const match = co?.co_number?.match(/\.AV(\d+)$/i);
            const num = match ? parseInt(match?.[1], 10) : 0;
            return num > max ? num : max;
          }, 0);

          let maxAv = Math.max(maxAvInMemory, maxAvInDb);
          const coNumber = `${costCode}.AV${maxAv + 1}`;
          const now = new Date()?.toISOString();

          const { data: newCo, error: insertError } = await supabase
            ?.from('subcontract_change_orders')
            ?.insert({
              project_id: projectId,
              subcontract_id: line?.subcontract_id,
              source_extra_id: extra?.id,
              co_number: coNumber,
              description: extra?.title || null,
              amount: Number(line?.amount) || 0,
              status: 'draft',
              approved_at: null,
            })
            ?.select(`
              *,
              subcontract:subcontracts(
                id,
                contract_value,
                subcontractors(id, company_name, trade),
                project_costs(id, cost_code, description)
              ),
              source_extra:project_extras(id, title, type, status, co_number, directive_id, ${EXTRA_DIRECTIVE_EMBED})
            `)
            ?.single();

          if (insertError) {
            console.error('Backfill: error inserting CO:', insertError?.message);
            continue;
          }

          if (newCo) {
            const mapped = {
              ...Object.fromEntries(
                Object.entries(newCo)?.map(([k, v]) => [
                  k?.replace(/_([a-z])/g, (_, l) => l?.toUpperCase()),
                  v,
                ])
              ),
              subcontract: newCo?.subcontract ? {
                ...Object.fromEntries(
                  Object.entries(newCo?.subcontract)?.map(([k, v]) => [
                    k?.replace(/_([a-z])/g, (_, l) => l?.toUpperCase()),
                    v,
                  ])
                ),
                subcontractor: newCo?.subcontract?.subcontractors
                  ? Object.fromEntries(
                      Object.entries(newCo?.subcontract?.subcontractors)?.map(([k, v]) => [
                        k?.replace(/_([a-z])/g, (_, l) => l?.toUpperCase()),
                        v,
                      ])
                    )
                  : null,
                projectCost: newCo?.subcontract?.project_costs
                  ? Object.fromEntries(
                      Object.entries(newCo?.subcontract?.project_costs)?.map(([k, v]) => [
                        k?.replace(/_([a-z])/g, (_, l) => l?.toUpperCase()),
                        v,
                      ])
                    )
                  : null,
              } : null,
              sourceExtra: newCo?.source_extra
                ? Object.fromEntries(
                    Object.entries(newCo?.source_extra)?.map(([k, v]) => [
                      k?.replace(/_([a-z])/g, (_, l) => l?.toUpperCase()),
                      v,
                    ])
                  )
                : null,
            };
            updatedCos = [mapped, ...updatedCos];
          }
        }
      }

      return updatedCos;
    } catch (err) {
      console.error('Backfill missing change orders error:', err);
      return existingCos;
    }
  };

  // Backfill co_number for COs that were auto-created without one
  const backfillMissingCoNumbers = async (cos, subs) => {
    const missing = cos?.filter(co => !co?.coNumber);
    if (!missing?.length) return cos;

    const updated = [...cos];

    // Group missing COs by subcontract_id to assign sequential numbers
    // We need to assign numbers that don't conflict with existing ones
    const subcontractGroups = {};
    for (const co of missing) {
      if (!subcontractGroups?.[co?.subcontractId]) {
        subcontractGroups[co?.subcontractId] = [];
      }
      subcontractGroups?.[co?.subcontractId]?.push(co);
    }

    for (const subcontractId of Object.keys(subcontractGroups)) {
      const subcontract = subs?.find(s => s?.id === subcontractId);
      let costCode = subcontract?.projectCost?.costCode || 'CO';

      // Find max AV number already assigned for this subcontract (from already-numbered COs)
      const numberedForSub = cos?.filter(co => co?.subcontractId === subcontractId && co?.coNumber);
      let maxAv = numberedForSub?.reduce((max, co) => {
        const match = co?.coNumber?.match(/\.AV(\d+)$/i);
        const num = match ? parseInt(match?.[1], 10) : 0;
        return num > max ? num : max;
      }, 0);

      // Sort missing COs for this subcontract by created_at to assign numbers in order
      const missingForSub = subcontractGroups?.[subcontractId]?.sort(
        (a, b) => new Date(a?.createdAt || 0) - new Date(b?.createdAt || 0)
      );

      for (const co of missingForSub) {
        maxAv += 1;
        const newCoNumber = `${costCode}.AV${maxAv}`;

        try {
          await supabase
            ?.from('subcontract_change_orders')
            ?.update({ co_number: newCoNumber })
            ?.eq('id', co?.id);

          // Update in local array
          const idx = updated?.findIndex(c => c?.id === co?.id);
          if (idx !== -1) {
            updated[idx] = { ...updated?.[idx], coNumber: newCoNumber };
          }
        } catch (err) {
          console.error('Error backfilling co_number:', err);
        }
      }
    }

    return updated;
  };

  const handleAdd = async (formData) => {
    // Auto-generate CO number: COSTCODE.AV#
    // Uses MAX of existing AV numbers (not COUNT) to prevent duplicates after deletions
    const subcontract = subcontracts?.find(s => s?.id === formData?.subcontractId);
    let costCode = subcontract?.projectCost?.costCode || 'CO';

    // Find all COs for this subcontract_id, extract numeric AV part, take the MAX
    const existingForSubcontract = changeOrders?.filter(co => co?.subcontractId === formData?.subcontractId);
    let maxAv = existingForSubcontract?.reduce((max, co) => {
      const match = co?.coNumber?.match(/\.AV(\d+)$/i);
      const num = match ? parseInt(match?.[1], 10) : 0;
      return num > max ? num : max;
    }, 0);
    const nextNum = maxAv + 1;
    const autoCoNumber = `${costCode}.AV${nextNum}`;

    await subcontractChangeOrderService?.create({ ...formData, coNumber: autoCoNumber });
    await fetchAll();
  };

  const handleEdit = async (formData) => {
    await subcontractChangeOrderService?.update(editData?.id, formData);
    await fetchAll();
  };

  // Fiche d'avenant (consultation -> Modifier) : enregistre puis renvoie la
  // ligne a jour, que la fiche affiche aussitot.
  const handleSaveAvenant = async (coId, formData) => {
    const updated = await subcontractChangeOrderService?.update(coId, formData);
    await fetchAll();
    return updated;
  };

  const handleOpenEdit = (co) => {
    setEditData(co);
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditData(null);
  };

  const handleDelete = (coId) => {
    setDeleteTargetId(coId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    try {
      await subcontractChangeOrderService?.delete(deleteTargetId);
      setChangeOrders(prev => prev?.filter(co => co?.id !== deleteTargetId));
    } catch (err) {
      console.error('Error deleting change order:', err);
    } finally {
      setShowDeleteModal(false);
      setDeleteTargetId(null);
    }
  };

  const handleSend = async (coId) => {
    try {
      const updated = await subcontractChangeOrderService?.updateStatus(coId, 'sent');
      if (updated) {
        setChangeOrders(prev => prev?.map(co => co?.id === coId ? updated : co));
      }
    } catch (err) {
      console.error('Error sending change order:', err);
    }
  };

  const handleApprove = async (coId) => {
    try {
      const updated = await subcontractChangeOrderService?.updateStatus(coId, 'approved');
      if (updated) {
        setChangeOrders(prev => prev?.map(co => co?.id === coId ? updated : co));
      }
    } catch (err) {
      console.error('Error approving change order:', err);
    }
  };

  const handleReject = async (coId) => {
    try {
      const updated = await subcontractChangeOrderService?.updateStatus(coId, 'rejected');
      if (updated) {
        setChangeOrders(prev => prev?.map(co => co?.id === coId ? updated : co));
      }
    } catch (err) {
      console.error('Error rejecting change order:', err);
    }
  };

  const handleMarkAsSigned = async (coId) => {
    try {
      const updated = await subcontractChangeOrderService?.updateStatus(coId, 'signed');
      if (updated) {
        setChangeOrders(prev => prev?.map(co => co?.id === coId ? updated : co));
      }
    } catch (err) {
      console.error('Error marking change order as signed:', err);
    }
  };

  const handleStatusChange = async (coId, newStatus) => {
    try {
      const updated = await subcontractChangeOrderService?.updateStatus(coId, newStatus);
      if (updated) {
        setChangeOrders(prev => prev?.map(co => co?.id === coId ? updated : co));
      }
    } catch (err) {
      console.error('Error updating change order status:', err);
    }
  };

  const handleNoteUpdate = async (coId, noteText) => {
    try {
      const updated = await subcontractChangeOrderService?.update(coId, { notes: noteText?.trim() || null });
      if (updated) {
        setChangeOrders(prev => prev?.map(co => co?.id === coId ? updated : co));
      }
      return true;
    } catch (err) {
      console.error('Error updating change order note:', err);
      return false;
    }
  };

  const handleNavigateToExtra = (extraId) => {
    navigate(`/project/${id}/extras?highlight=${extraId}`);
  };

  const calculateKPIs = () => {
    const total = changeOrders?.length || 0;
    const approved = changeOrders?.filter(co => co?.status === 'signed')?.length || 0;
    const pending = changeOrders?.filter(co => co?.status === 'sent')?.length || 0;
    const approvedAmount = changeOrders
      ?.filter(co => co?.status === 'signed')
      ?.reduce((sum, co) => sum + (parseFloat(co?.amount) || 0), 0) || 0;
    return { total, approved, pending, approvedAmount };
  };

  const kpis = calculateKPIs();

  // Helper: parse a CO number like "09660.AV10" into { costCode: "09660", avNum: 10 }
  const parseCoNumber = (coNumber) => {
    if (!coNumber) return { costCode: '', avNum: 0 };
    const match = coNumber?.match(/^(.+?)\.(?:[A-Za-z]+)(\d+)$/);
    if (match) {
      return { costCode: match?.[1]?.toLowerCase(), avNum: parseInt(match?.[2], 10) };
    }
    return { costCode: coNumber?.toLowerCase(), avNum: 0 };
  };

  const sortedCOs = [...(changeOrders || [])]?.sort((a, b) => {
    if (!sortConfig?.key) return 0;
    let aVal, bVal;
    switch (sortConfig?.key) {
      case 'co_number': {
        const aParsed = parseCoNumber(a?.coNumber);
        const bParsed = parseCoNumber(b?.coNumber);
        // Sort by cost code first, then by numeric AV suffix
        if (aParsed?.costCode < bParsed?.costCode) return sortConfig?.direction === 'asc' ? -1 : 1;
        if (aParsed?.costCode > bParsed?.costCode) return sortConfig?.direction === 'asc' ? 1 : -1;
        const numDiff = aParsed?.avNum - bParsed?.avNum;
        return sortConfig?.direction === 'asc' ? numDiff : -numDiff;
      }
      case 'subcontractor': aVal = a?.subcontract?.subcontractor?.companyName?.toLowerCase() || ''; bVal = b?.subcontract?.subcontractor?.companyName?.toLowerCase() || ''; break;
      case 'trade': aVal = a?.subcontract?.subcontractor?.trade?.toLowerCase() || ''; bVal = b?.subcontract?.subcontractor?.trade?.toLowerCase() || ''; break;
      case 'amount': aVal = parseFloat(a?.amount) || 0; bVal = parseFloat(b?.amount) || 0; break;
      case 'status': aVal = a?.status || ''; bVal = b?.status || ''; break;
      case 'created_at': aVal = new Date(a?.createdAt || 0)?.getTime(); bVal = new Date(b?.createdAt || 0)?.getTime(); break;
      default: return 0;
    }
    if (aVal < bVal) return sortConfig?.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig?.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const filteredCOs = (() => {
    let list = sortedCOs;
    // Apply subcontract filter from URL param
    if (subcontractFilter) {
      list = list?.filter(co => co?.subcontractId === subcontractFilter);
    }
    // Apply dropdown subcontractor filter
    if (selectedSubcontractorId) {
      list = list?.filter(co => co?.subcontractId === selectedSubcontractorId);
    }
    // Apply status filter
    if (statusFilter !== 'all') {
      list = list?.filter(co => (co?.status || 'draft') === statusFilter);
    }
    return list;
  })();

  const formatCurrency = (val) => fmtCAD(val);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Icon name="Loader2" size={48} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="pt-0 pb-0 px-0 h-full flex flex-col">
      {/* Header Block */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-6">
          {/* Left: Title */}
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="GitBranch" size={20} className="text-primary" />
              <h1 className="text-lg font-semibold text-gray-900">{t('changeOrder.page.title')}</h1>
            </div>
            <div className="flex items-center gap-1 mb-1">
              <span
                onClick={() => navigate('/project-detail')}
                className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
              >
                {project?.projectNumber || 'N/A'}
              </span>
              <span className="text-sm text-primary font-normal">-</span>
              <span
                onClick={() => navigate('/project-detail')}
                className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
              >
                {project?.name || 'Loading...'}
              </span>
            </div>
            <p className="text-xs text-gray-500">{t('changeOrder.page.subtitle')}</p>
          </div>

          {/* Right: KPI Cards */}
          <div className="flex-1 flex items-center justify-end gap-2">
            <StatsCard
              title={t('changeOrders.kpi.total')}
              value={kpis?.total}
              iconName="FileText"
              iconColor="text-blue-500"
              valueColor="text-blue-500"
              titleWeight="font-medium"
              sublabel=""
            />
            <StatsCard
              title={t('changeOrders.kpi.pending')}
              value={kpis?.pending}
              iconName="Clock"
              iconColor="text-orange-500"
              valueColor="text-gray-900"
              titleWeight="font-medium"
              sublabel=""
            />
            <StatsCard
              title={t('changeOrders.kpi.approved')}
              value={kpis?.approved}
              iconName="CheckCircle2"
              iconColor="text-green-500"
              valueColor="text-green-600"
              titleWeight="font-medium"
              sublabel=""
            />
            <StatsCard
              title={t('changeOrders.kpi.approvedAmount')}
              value={formatCurrency(kpis?.approvedAmount)}
              iconName="DollarSign"
              iconColor="text-green-500"
              valueColor="text-gray-900"
              titleWeight="font-medium"
              sublabel=""
            />
          </div>
        </div>
      </div>
      {/* Search + Add Row */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <SubcontractorDropdown
            subcontracts={subcontracts}
            value={selectedSubcontractorId}
            onChange={setSelectedSubcontractorId}
          />
          <StatusFilterDropdown
            options={changeOrderStatusFilterOptions}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </div>
        <button
          onClick={() => { setEditData(null); setShowAddModal(true); }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors shadow-sm"
        >
          <Icon name="Plus" size={13} />
          {t('changeOrders.addButton')}
        </button>
      </div>
      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden min-h-0 flex flex-col">
        {subcontractFilter && (
          <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 border-b border-orange-100">
            <Icon name="Filter" size={13} className="text-primary" />
            <span className="text-xs text-gray-700">
              {t('avenants.filteredBySubcontract')}
            </span>
            <button
              onClick={() => setSearchParams(prev => { const p = new URLSearchParams(prev); p?.delete('subcontract'); return p; }, { replace: true })}
              className="ml-auto text-xs text-primary hover:text-primary-dark flex items-center gap-1"
            >
              <Icon name="X" size={11} />
              {t('avenants.clearFilter')}
            </button>
          </div>
        )}
        <div className="min-h-0 overflow-y-auto overflow-x-auto overscroll-contain">
          <ChangeOrdersTable
            changeOrders={filteredCOs}
            onEdit={handleOpenEdit}
            onDelete={handleDelete}
            onSend={handleSend}
            onMarkAsSigned={handleMarkAsSigned}
            onStatusChange={handleStatusChange}
            sortConfig={sortConfig}
            onSort={handleSort}
            onNavigateToExtra={handleNavigateToExtra}
            projectId={id}
            onNoteUpdate={handleNoteUpdate}
            subcontracts={subcontracts}
            projectExtras={projectExtras}
            onSaveAvenant={handleSaveAvenant}
          />
        </div>
      </div>
      {/* Modals */}
      <AddChangeOrderModal
        isOpen={showAddModal}
        onClose={handleCloseModal}
        onSubmit={editData ? handleEdit : handleAdd}
        projectId={id}
        subcontracts={subcontracts}
        projectExtras={projectExtras}
        editData={editData}
      />
      <ConfirmDialog
        isOpen={showDeleteModal}
        onCancel={() => { setShowDeleteModal(false); setDeleteTargetId(null); }}
        onConfirm={confirmDelete}
        title={t('changeOrders.delete.title')}
        message={t('changeOrders.delete.message')}
        confirmLabel={t('changeOrders.delete.confirm')}
        cancelLabel={t('changeOrders.delete.cancel')}
        confirmVariant="danger"
      />
    </div>
  );
};

export default SubcontractChangeOrdersPage;
